import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ClipboardList, RefreshCw, Search as SearchIcon, Filter, ShieldAlert,
  FileMusic, Hourglass, Plus, Building2, Upload, Play, Pause, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, buildAudioUrl, buildDemoAudioUrl } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Slider } from "@/components/ui/slider";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { InternalDemoIntakeDialog } from "@/components/InternalDemoIntakeDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_COLOUR = {
  "Demo Received":              { bg: "hsl(220 80% 92%)", fg: "hsl(220 80% 28%)" },
  "AI Origin Screening":        { bg: "hsl(40 70% 92%)",  fg: "hsl(38 70% 28%)" },
  "Documentation Review":       { bg: "hsl(190 50% 92%)", fg: "hsl(190 50% 26%)" },
  "Human Authorship Review":    { bg: "hsl(280 40% 92%)", fg: "hsl(280 40% 28%)" },
  "Rights & Contract Review":   { bg: "hsl(20 35% 90%)",  fg: "hsl(20 35% 26%)" },
  "Development Approved":       { bg: "hsl(135 35% 90%)", fg: "hsl(135 35% 24%)" },
  "Re-Production In Progress":  { bg: "hsl(160 30% 90%)", fg: "hsl(160 30% 24%)" },
  "New Master Delivered":       { bg: "hsl(155 35% 88%)", fg: "hsl(155 35% 22%)" },
  "Rights & Metadata Clearance":{ bg: "hsl(45 50% 90%)",  fg: "hsl(40 50% 24%)" },
  "Catalogue Eligible":         { bg: "hsl(40 48% 88%)",  fg: "hsl(347 60% 22%)" },
  "Archived / Declined / On Hold": { bg: "hsl(0 30% 92%)",fg: "hsl(0 30% 28%)" },
};

const CONTRACT_COLOUR = {
  "Approved":                    { bg: "hsl(135 35% 90%)", fg: "hsl(135 35% 22%)" },
  "Under Review":                { bg: "hsl(45 60% 90%)",  fg: "hsl(40 60% 24%)" },
  "Required":                    { bg: "hsl(20 35% 90%)",  fg: "hsl(20 35% 24%)" },
  "Requested":                   { bg: "hsl(220 50% 92%)", fg: "hsl(220 50% 28%)" },
  "Partially Received":          { bg: "hsl(40 50% 92%)",  fg: "hsl(40 50% 24%)" },
  "Rejected":                    { bg: "hsl(0 40% 92%)",   fg: "hsl(0 40% 26%)" },
  "Missing Critical Documents":  { bg: "hsl(0 35% 90%)",   fg: "hsl(0 35% 24%)" },
  "Not Required Yet":            { bg: "hsl(225 14% 92%)", fg: "hsl(225 14% 28%)" },
};

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function fmtClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function PaletteBadge({ value, palette, fallbackKey, testid }) {
  if (!value) return <span className="text-muted-foreground">{"\u2014"}</span>;
  const s = palette[value] || palette[fallbackKey] || { bg: "hsl(225 14% 92%)", fg: "hsl(225 14% 28%)" };
  return (
    <Badge
      variant="outline"
      style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
      className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
      data-testid={testid}
    >
      {value}
    </Badge>
  );
}

const HEADLINE_COUNTERS = [
  { key: "total",          label: "Total" },
  { key: "received",       label: "Received",          workflow: "Demo Received" },
  { key: "ai_screening",   label: "Screening",         workflow: "AI Origin Screening" },
  { key: "human_review",   label: "Human Review",      workflow: "Human Authorship Review" },
  { key: "rights_contract",label: "Rights / Contract", workflow: "Rights & Contract Review" },
  { key: "dev_approved",   label: "Dev Approved",      workflow: "Development Approved" },
  { key: "catalogue",      label: "Catalogue",         workflow: "Catalogue Eligible" },
  { key: "holds",          label: "On Hold",           hold: true },
  { key: "archived",       label: "Archived",          workflow: "Archived / Declined / On Hold" },
];

export default function Demos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";
  const writer = canWrite(user);
  const canPromote = ["super_admin", "gfmp_reviewer"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({
    workflow_stages: [],
    screening_statuses: [],
    risk_levels: [],
    contract_states: [],
  });

  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [workflow, setWorkflow] = useState("all");
  const [screening, setScreening] = useState("all");
  const [risk, setRisk] = useState("all");
  const [contract, setContract] = useState("all");
  const [holdFilter, setHoldFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);

  // ---- DFR promotion (role-gated; server enforces) ------------------------
  const [codes, setCodes] = useState([]);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [destCode, setDestCode] = useState("");
  const [promoteReason, setPromoteReason] = useState("");
  const [promoting, setPromoting] = useState(false);

  // ---- Single shared audition player (one demo plays at a time) -----------
  const audioRef = useRef(null);
  const [player, setPlayer] = useState({ id: null, playing: false, t: 0, dur: 0 });

  useEffect(() => {
    const a = new Audio();
    a.preload = "none"; // no autoplay, no eager fetch
    audioRef.current = a;
    const onTime = () => setPlayer((p) => ({ ...p, t: a.currentTime || 0 }));
    const onDur = () => setPlayer((p) => ({ ...p, dur: Number.isFinite(a.duration) ? a.duration : 0 }));
    const onPlay = () => setPlayer((p) => ({ ...p, playing: true }));
    const onPause = () => setPlayer((p) => ({ ...p, playing: false }));
    const onEnded = () => setPlayer((p) => ({ ...p, playing: false, t: 0 }));
    const onError = () => {
      setPlayer({ id: null, playing: false, t: 0, dur: 0 });
      toast.error("Audio playback failed \u2014 the file may be unavailable.");
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("durationchange", onDur);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("error", onError);
    return () => {
      a.pause();
      a.removeAttribute("src");
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("durationchange", onDur);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("error", onError);
    };
  }, []);

  const togglePlay = useCallback((row) => {
    const a = audioRef.current;
    if (!a) return;
    if (player.id !== row.id) {
      // Starting another demo stops the current one.
      a.pause();
      a.src = row.record_kind === "dfr_track" ? buildAudioUrl(row.id) : buildDemoAudioUrl(row.id);
      setPlayer({ id: row.id, playing: false, t: 0, dur: 0 });
      a.play().catch(() => {
        setPlayer({ id: null, playing: false, t: 0, dur: 0 });
        toast.error("Audio playback failed \u2014 the file may be unavailable.");
      });
    } else if (a.paused) {
      a.play().catch(() => toast.error("Audio playback failed."));
    } else {
      a.pause();
    }
  }, [player.id]);

  const seekTo = useCallback((value) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(value)) return;
    a.currentTime = value;
    setPlayer((p) => ({ ...p, t: value }));
  }, []);

  const refresh = useCallback(async () => {
    if (!writer) return;
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (workflow !== "all") params.workflow_status = workflow;
      if (screening !== "all") params.screening_status = screening;
      if (risk !== "all") params.origin_risk = risk;
      if (contract !== "all") params.contract_packet_status = contract;
      if (holdFilter === "yes") params.compliance_hold = true;
      if (holdFilter === "no")  params.compliance_hold = false;
      const r = await api.get("/demos", { params });
      let rows = Array.isArray(r.data) ? r.data : [];
      // Client-side visibility filter — supported regardless of server changes.
      if (visibilityFilter === "internal_only") {
        rows = rows.filter((d) => d.visibility === "internal_only");
      } else if (visibilityFilter === "public_submission") {
        rows = rows.filter((d) => (d.visibility || "public_submission") === "public_submission");
      }
      setItems(rows);
    } catch (e) {
      toast.error(errMsg(e, "Could not load demos."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [writer, q, workflow, screening, risk, contract, holdFilter, visibilityFilter]);

  useEffect(() => {
    if (!writer) return;
    refresh();
    api.get("/demos/meta")
      .then((r) => setMeta((m) => ({ ...m, ...(r.data || {}) })))
      .catch(() => {});
  }, [writer, refresh]);

  useEffect(() => {
    if (canPromote) {
      api.get("/catalog-codes", { params: { active_only: true } })
        .then((r) => setCodes((r.data || []).filter((c) => c.code !== "DFR")))
        .catch(() => {});
    }
  }, [canPromote]);

  function submitSearch(e) {
    e?.preventDefault?.();
    setQ(searchInput.trim());
  }

  async function promote() {
    if (!destCode) { toast.error("Choose a destination Catalog Code"); return; }
    setPromoting(true);
    try {
      const r = await api.post(`/tracks/${promoteTarget.id}/promote-to-catalog`, {
        destination_catalog_code: destCode, reason: promoteReason,
      });
      toast.success(`${r.data.track_id} promoted to catalog (${r.data.catalog_code})`);
      setPromoteTarget(null); setDestCode(""); setPromoteReason("");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPromoting(false); }
  }

  const counters = useMemo(() => {
    const out = { total: items.length, holds: 0 };
    HEADLINE_COUNTERS.forEach((c) => { if (!out[c.key]) out[c.key] = 0; });
    items.forEach((it) => {
      const w = it.workflow_status;
      HEADLINE_COUNTERS.forEach((c) => {
        if (c.workflow && w === c.workflow) out[c.key] = (out[c.key] || 0) + 1;
      });
      if (it?.compliance_hold?.placed) out.holds += 1;
    });
    return out;
  }, [items]);

  if (!writer) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="demos-readonly">
        <Alert>
          <AlertDescription>
            The Demo &amp; Development Intake Register is visible to Catalog Manager, GFMP Reviewer, and Super Admin only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="demos-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Kandake Compliance · Demo &amp; Development Intake
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <ClipboardList className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Demo Register
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            The complete register of demo-designated material — public submissions, internal intake,
            and DFR (Demo For Review) audio records from Bulk Audio Intake. All material is held privately;
            screening results are internal only and must never be relayed verbatim to submitters.
            DFR records are excluded from Catalog Tracks until promoted by a GFMP Reviewer or Super Admin.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="demos-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isSuperAdmin && (
            <Button variant="outline" size="sm" onClick={() => navigate("/intake?mode=audio&dest=demo")} data-testid="demos-bulk-audio-intake">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Bulk Audio Intake
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setIntakeDialogOpen(true)}
            data-testid="demos-new-internal-intake"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Internal Demo Intake
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pipeline counts</CardTitle>
          <CardDescription>Aggregated across the current filtered result set.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="demos-counters">
          {HEADLINE_COUNTERS.map((c) => (
            <div key={c.key} className="rounded-md border p-2.5" data-testid={`demos-count-${c.key}`}>
              <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{c.label}</div>
              <div className="font-display text-xl" style={{ color: "hsl(347 60% 22%)" }}>
                {counters[c.key] ?? 0}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-4">
          <form onSubmit={submitSearch} className="flex flex-wrap items-end gap-3" data-testid="demos-filter-form">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Search (title, filename, submitter, email, reference, ID)
              </label>
              <div className="flex items-center gap-2">
                <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search…"
                  data-testid="demos-search"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Workflow</label>
              <Select value={workflow} onValueChange={setWorkflow}>
                <SelectTrigger className="w-[230px]" data-testid="demos-workflow-filter">
                  <Filter className="w-3.5 h-3.5 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All workflow stages</SelectItem>
                  {(meta.workflow_stages || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Screening</label>
              <Select value={screening} onValueChange={setScreening}>
                <SelectTrigger className="w-[200px]" data-testid="demos-screening-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any screening status</SelectItem>
                  {(meta.screening_statuses || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Risk</label>
              <Select value={risk} onValueChange={setRisk}>
                <SelectTrigger className="w-[200px]" data-testid="demos-risk-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any risk level</SelectItem>
                  {(meta.risk_levels || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Contract</label>
              <Select value={contract} onValueChange={setContract}>
                <SelectTrigger className="w-[200px]" data-testid="demos-contract-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any contract state</SelectItem>
                  {(meta.contract_states || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Hold</label>
              <Select value={holdFilter} onValueChange={setHoldFilter}>
                <SelectTrigger className="w-[160px]" data-testid="demos-hold-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="yes">Hold active</SelectItem>
                  <SelectItem value="no">No hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Source</label>
              <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                <SelectTrigger className="w-[170px]" data-testid="demos-visibility-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Public + Internal</SelectItem>
                  <SelectItem value="public_submission">Public only</SelectItem>
                  <SelectItem value="internal_only">Internal only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" data-testid="demos-apply">Apply</Button>
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => {
                setSearchInput(""); setQ("");
                setWorkflow("all"); setScreening("all"); setRisk("all");
                setContract("all"); setHoldFilter("all"); setVisibilityFilter("all");
              }}
              data-testid="demos-clear"
            >
              Clear
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="demos-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">Received</TableHead>
                <TableHead className="w-[120px]">Reference</TableHead>
                <TableHead>Recording / submitter</TableHead>
                <TableHead className="w-[250px]">Audio</TableHead>
                <TableHead className="w-[190px]">Workflow</TableHead>
                <TableHead className="w-[160px]">Contract</TableHead>
                <TableHead className="w-[90px]">Hold</TableHead>
                <TableHead className="w-[190px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    <span className="inline-flex items-center gap-2">
                      <Hourglass className="w-3.5 h-3.5" /> Loading…
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-10" data-testid="demos-empty">
                    No demo submissions match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((d) => {
                const isDfr = d.record_kind === "dfr_track";
                const active = player.id === d.id;
                return (
                <TableRow key={d.id} data-testid={`demos-row-${d.id}`}>
                  <TableCell className="text-[12px]">
                    <div>{fmtDate(d.created_at)}</div>
                  </TableCell>
                  <TableCell className="font-mono text-[12px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`demos-ref-${d.id}`}>{d.ref_id || "\u2014"}</span>
                      {isDfr && (
                        <Badge
                          variant="outline"
                          style={{ background: "hsl(347 40% 93%)", color: "hsl(347 60% 22%)", borderColor: "hsl(347 60% 22% / 0.33)" }}
                          className="text-[9px] uppercase tracking-wide font-medium whitespace-nowrap"
                          data-testid={`demos-dfr-badge-${d.id}`}
                        >
                          DFR
                        </Badge>
                      )}
                      {!isDfr && d.visibility === "internal_only" && (
                        <Badge
                          variant="outline"
                          style={{ background: "hsl(40 48% 92%)", color: "hsl(347 60% 22%)", borderColor: "hsl(347 60% 22% / 0.33)" }}
                          className="text-[9px] uppercase tracking-wide font-medium whitespace-nowrap"
                          data-testid={`demos-internal-badge-${d.id}`}
                        >
                          <Building2 className="w-3 h-3 mr-1" /> Internal
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[12.5px]">
                    <div className="font-medium flex items-center gap-2">
                      <FileMusic className="w-3.5 h-3.5 opacity-60" />
                      <span className="truncate max-w-[240px]" title={d.song_title}>{d.song_title || "(untitled)"}</span>
                    </div>
                    <div className="text-muted-foreground text-[11px] truncate max-w-[260px]" title={d.email || d.audio_original_filename}>
                      {isDfr
                        ? (d.audio_original_filename || "Bulk Audio Intake")
                        : (
                          <>
                            {d.submitter_full_legal_name || "\u2014"}
                            {d.email ? ` · ${d.email}` : ""}
                          </>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {d.has_audio ? (
                      <div className="flex items-center gap-2" data-testid={`demos-audio-${d.id}`}>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7 shrink-0"
                          onClick={() => togglePlay(d)}
                          aria-label={active && player.playing ? "Pause" : "Play"}
                          data-testid={`demos-audio-toggle-${d.id}`}
                        >
                          {active && player.playing
                            ? <Pause className="w-3.5 h-3.5" />
                            : <Play className="w-3.5 h-3.5" />}
                        </Button>
                        {active ? (
                          <div className="flex items-center gap-2 min-w-[150px]" data-testid={`demos-audio-player-${d.id}`}>
                            <Slider
                              value={[Math.min(player.t, player.dur || player.t)]}
                              max={player.dur || 1}
                              step={0.5}
                              onValueChange={(v) => seekTo(v[0])}
                              className="w-[90px]"
                              data-testid={`demos-audio-seek-${d.id}`}
                            />
                            <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`demos-audio-time-${d.id}`}>
                              {fmtClock(player.t)} / {fmtClock(player.dur)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground truncate max-w-[140px]" title={d.audio_original_filename}>
                            {d.audio_original_filename || "Audio attached"}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground" data-testid={`demos-audio-unavailable-${d.id}`}>
                        Audio unavailable
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <PaletteBadge value={d.workflow_status} palette={STATUS_COLOUR} fallbackKey="Demo Received" testid={`demos-workflow-${d.id}`} />
                  </TableCell>
                  <TableCell>
                    <PaletteBadge value={d.contract_packet_status} palette={CONTRACT_COLOUR} fallbackKey="Not Required Yet" testid={`demos-contract-${d.id}`} />
                  </TableCell>
                  <TableCell>
                    {d?.compliance_hold?.placed ? (
                      <Badge
                        variant="outline"
                        style={{ background: "hsl(0 40% 92%)", color: "hsl(0 40% 26%)", borderColor: "hsl(0 40% 26% / 0.33)" }}
                        className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
                        data-testid={`demos-hold-${d.id}`}
                      >
                        <ShieldAlert className="w-3 h-3 mr-1" /> Hold
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-[12px]" data-testid={`demos-no-hold-${d.id}`}>{"\u2014"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isDfr ? (
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {canPromote ? (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => { setPromoteTarget(d); setDestCode(""); setPromoteReason(""); }}
                            data-testid={`dfr-promote-${d.ref_id}`}
                          >
                            Promote to Catalog
                          </Button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground" data-testid={`dfr-no-promote-${d.ref_id}`}>
                            Reviewer approval required
                          </span>
                        )}
                        <Button asChild size="sm" variant="ghost" data-testid={`demos-open-${d.id}`}>
                          <Link to={`/tracks/${d.id}`}><ArrowUpRight className="w-3.5 h-3.5" /></Link>
                        </Button>
                      </div>
                    ) : (
                      <Button asChild size="sm" variant="ghost" data-testid={`demos-open-${d.id}`}>
                        <Link to={`/demos/${d.id}`}>Open</Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DFR — Promote to Catalog (server-enforced: gfmp_reviewer / super_admin) */}
      <Dialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Promote to Catalog</DialogTitle>
            <DialogDescription>
              Moves {promoteTarget?.ref_id} · "{promoteTarget?.song_title}" out of the Demo Register into
              Catalog Tracks. The existing record and audio asset are updated — nothing is duplicated.
              Compliance gates still apply after promotion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Destination Catalog Code *</Label>
              <Select value={destCode} onValueChange={setDestCode}>
                <SelectTrigger data-testid="dfr-promote-dest-select"><SelectValue placeholder="Choose destination…" /></SelectTrigger>
                <SelectContent>
                  {codes.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={2} value={promoteReason} onChange={(e) => setPromoteReason(e.target.value)} data-testid="dfr-promote-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteTarget(null)}>Cancel</Button>
            <Button onClick={promote} disabled={promoting} style={{ background: "hsl(347 72% 22%)" }} data-testid="dfr-promote-confirm">
              {promoting ? "Promoting…" : "Approve & Promote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InternalDemoIntakeDialog
        open={intakeDialogOpen}
        onOpenChange={setIntakeDialogOpen}
        meta={meta}
        onCreated={() => { refresh(); }}
      />
    </div>
  );
}
